import React, {useEffect, useState} from "react"
import {IUserPreferences} from "../../../../Models/IUserPreferences";
import {BuxTooltip} from "../../../Common";
import {Divider, Row, InputNumber, Checkbox, Input, Radio, notification} from "antd";
import {IUser} from "../../../../Models/IUser";
import Title from "antd/es/typography/Title";
import Button from "antd/lib/button";

interface TimeoutManagementProps {
    user: IUser;
    userPreferences: IUserPreferences;
    getUserPreferences: () => void;
    updateSessionTimeoutLength: (companyId: number, newSessionMinute: number) => void;
}

export const TimeoutManagement = (props: TimeoutManagementProps) => {
    const {
        userPreferences,
        user,
        getUserPreferences,
        updateSessionTimeoutLength
    } = props;

    /** S T A T E */
    // setting the current session timeout length
    const [currentSessionTimeoutLength, setCurrentSessionTimeoutLength] = useState<number>(0);
    // determine if the user is setting a new session timeout length
    const [isSettingNewSessionTimeoutLength, setIsSettingNewSessionTimeoutLength] = useState<boolean>(false);
    // determining if the user is using preset options
    const [isUsingPresetOptions, setIsUsingPresetOptions] = useState<boolean>(true);
    // setting the radio value
    const [radioValue, setRadioValue] = useState<number>(1);
    // setting the new session minute
    const [newSessionMinute, setNewSessionMinute] = useState<number>(0);
    // setting the new session hour
    const [newSessionHour, setNewSessionHour] = useState<number>(0);
    // determine if the user is updating the session timeout length
    const [isUpdatingValue, setIsUpdatingValue] = useState<boolean>(false);
    // getting the company id from the user object
    const companyId = user.company.cid;

    /** E F F E C T S */
    // making sure that userPreferences never comes back null
    useEffect(() => {
        userPreferences === null ? getUserPreferences() : setCurrentSessionTimeoutLength(userPreferences.sessionTimeoutLengthMinutes);
    }, [userPreferences]);

    // updating the isUpdatingValue state for disabling/enabling the button when the newSessionMinute or newSessionHour changes
    useEffect(() => {
        (newSessionMinute > 0 || newSessionHour > 0) ? setIsUpdatingValue(true) : setIsUpdatingValue(false);
    }, [newSessionMinute, newSessionHour]);

    /** H A N D L E R S */
    // handling the checkbox change
    const handleCheckboxOnChange = (e: any) => {
        if(e.target.checked) {
            setIsSettingNewSessionTimeoutLength(true);
        } else {
            setIsSettingNewSessionTimeoutLength(false);
            setNewSessionMinute(0);
            setNewSessionHour(0);
        }
    }

    // handling the radio button change
    const handleRadioChange = (e: any) => {
        setRadioValue(e.target.value);
        if(radioValue === 1) {
            setIsUsingPresetOptions(false);
        } else {
            setIsUsingPresetOptions(true);
        }
        // resetting the new session timeout length to NOT maintain state
        setNewSessionMinute(0);
        setNewSessionHour(0);
    }

    // handling the input change for the new minute change
    const handleInputMinuteChange = (value: number) => {
        if(value) {
            setNewSessionMinute(value);
        } else {
            setNewSessionMinute(0);
        }
    }

    // handling the input change for the new hour change
    const handleInputHourChange = (value: number) => {
        if(value) {
            setNewSessionHour(value);
        } else {
            setNewSessionHour(0);
        }
    }

    // handling the update button click
    const handleUpdateClick = () => {
        // converting the new session timeout length to minute
        const totalLengthInMinutes = (newSessionHour * 60) + newSessionMinute;

        if(totalLengthInMinutes > 720) {
            notification.error({
                message: "Session Timeout Length Update Failed",
                description: "The maximum session timeout length is 12 hours or 720 minutes. Please retry with a lower timeout length.",
            })
            return;
        }
        // passing in new session timeout length
        updateSessionTimeoutLength(companyId, totalLengthInMinutes);
        // resetting the state
        setNewSessionMinute(0);
        setNewSessionHour(0);
        setIsSettingNewSessionTimeoutLength(false);
    }

    // converting minute to hour for title display
    const convertMinutesToHours = (minute: number) => {
        let hour = Math.floor(minute / 60);
        let remainingMinutes = minute % 60;

        return `${hour} hour(s) and ${remainingMinutes} minute(s)`;
    }

    /** C O M P O N E N T S */
        // creating the unique display for minute vs hour
    const currentSessionTimeoutHoursMinutes = () => {
            if(currentSessionTimeoutLength >= 60) {
                return (
                    <React.Fragment>
                        <Title level={4}>Current session timeout length: {convertMinutesToHours(currentSessionTimeoutLength)}</Title>
                    </React.Fragment>
                );
            } else {
                return (
                    <React.Fragment>
                        <Title level={4}>Current session timeout length: {currentSessionTimeoutLength} minute(s)</Title>
                    </React.Fragment>
                );
            }
        }

    // creating the radio button component for selecting custom or preset options
    const radioButtons: JSX.Element = (
        <React.Fragment>
            <Radio.Group onChange={handleRadioChange} value={radioValue}>
                <Radio value={1}>Preset</Radio>
                <Radio value={2}>Custom</Radio>
            </Radio.Group>
        </React.Fragment>
    );

    // creating the preset options for the new session timeout length
    const presetOptions: JSX.Element = (
        <React.Fragment>
            <Row>
                <Button className="button" size="large" style={{marginRight: '10px'}} onClick={() => setNewSessionMinute(15)}>15 min</Button>
                <Button className="button" size="large" style={{marginRight: '10px'}} onClick={() => setNewSessionMinute(30)}>30 min</Button>
                <Button className="button" size="large" onClick={() => setNewSessionHour(1)}>1 hour</Button>
            </Row>
            <br/>
            <Row>
                <Button className="button" size="large" style={{marginRight: '10px'}} onClick={() => setNewSessionHour(3)}>3 hour</Button>
                <Button className="button" size="large" style={{marginRight: '10px'}} onClick={() => setNewSessionHour(6)}>6 hour</Button>
                <Button className="button" size="large" onClick={() => setNewSessionHour(12)}>12 hour</Button>
            </Row>
        </React.Fragment>
    )

    // creating the custom options for the new session timeout length
    const customOptions: JSX.Element = (
        <React.Fragment>
            <Row>
                <InputNumber
                    onChange={handleInputHourChange}
                    size="large"
                    value={newSessionHour}
                    min={0}
                />
                <span style={{marginLeft: 10, marginRight: 20}}>
                    hour
                </span>

                <InputNumber
                    onChange={handleInputMinuteChange}
                    size="large"
                    value={newSessionMinute}
                    min={0}
                />
                <span style={{marginLeft: 10}}>
                    minute
                </span>
            </Row>
        </React.Fragment>
    )

    // creating the user timeout management component which serves as the base component
    const userTimeoutManagementComponent: JSX.Element = (
            <div className="input-container">
                <React.Fragment>
                    <Row>
                        Update the session timeout length to change the amount of time before a user is automatically logged out.
                        <BuxTooltip
                            placement="right"
                            content={
                                <React.Fragment>
                                    <p>
                                        This change will affect all users in the company and will be applied upon the next login.
                                    </p>
                                </React.Fragment>
                            }
                            trigger="hover"
                            style={{ marginLeft: "1rem" }}
                        />
                    </Row>
                    <Divider/>
                    <Row>
                        { currentSessionTimeoutHoursMinutes() }
                    </Row>
                    <br/>
                    <Row>
                        <Checkbox checked={isSettingNewSessionTimeoutLength} onChange={handleCheckboxOnChange}>
                            Update session timeout length
                        </Checkbox>
                    </Row>
                    <br/>
                    {
                        isSettingNewSessionTimeoutLength ? (
                            <React.Fragment>
                                <br/>
                                <Row>
                                    { radioButtons }
                                </Row>
                                <br/>
                                <Row>
                                    <div style={{marginTop: 20}}>
                                        { isUsingPresetOptions ? presetOptions : customOptions }
                                    </div>
                                </Row>
                                <br/>
                                <Row>
                                    <Button type="primary" style={{marginTop: 20}} onClick={handleUpdateClick} disabled={!isUpdatingValue}>Update</Button>
                                </Row>
                            </React.Fragment>
                        ) : null
                    }
                </React.Fragment>
            </div>
        )

    return (
        <div className="TimeoutManagement">
            <div className="top-container">
                <h2 className="settings-title">User Timeout</h2>
                { userTimeoutManagementComponent }
            </div>
        </div>
    )
}